import { useContext, useEffect, useState } from "react";
import { ToastContext } from "src/context/SocketContext";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

export const VARIANT_STYLES = {
  info: {
    color: "#185FA5",
    icon: (
      <InformationCircleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        style={{ color: "#185FA5" }}
      />
    ),
  },
  success: {
    color: "#3B6D11",
    icon: (
      <CheckCircleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        style={{ color: "#3B6D11" }}
      />
    ),
  },
  warning: {
    color: "#854F0B",
    icon: (
      <ExclamationTriangleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        style={{ color: "#854F0B" }}
      />
    ),
  },
  error: {
    color: "#A32D2D",
    icon: (
      <XCircleIcon
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        style={{ color: "#A32D2D" }}
      />
    ),
  },
};

const Toast = (props) => {
  const { id, actions: initialActions, removeToast } = props;
  const [actions, setActions] = useState(initialActions);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    setActions(initialActions);
  }, [initialActions]);

  const handleBatchSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);

    let currentActions = [...actions];
    let isAllSuccessful = true;

    for (let i = 0; i < currentActions.length; i++) {
      if (currentActions[i].status === "success") continue;

      setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, status: "loading" } : a)));

      try {
        await currentActions[i].handler();
        setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, status: "success" } : a)));
      } catch (err) {
        setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, status: "error" } : a)));
        isAllSuccessful = false;
      }
    }

    setIsSyncing(false);

    if (isAllSuccessful) {
      setTimeout(() => removeToast(id), 1200);
    }
  };

  return (
    <div className="pointer-events-auto flex w-[350px] flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-xl">
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
          <span className="text-[13px] font-bold text-gray-900">Updates Detected</span>
        </div>
        <button onClick={() => removeToast(id)}>
          <XMarkIcon className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      <div className="max-h-[200px] space-y-2 overflow-y-auto pr-1">
        {actions.map((action, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
            <span className="mr-2 truncate text-[12px] font-medium text-gray-600">{action.label}</span>
            <div className="flex-shrink-0">
              {action.status === "loading" && <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-500" />}
              {action.status === "success" && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
              {action.status === "error" && <XCircleIcon className="h-4 w-4 text-red-500" />}
              {action.status === "pending" && <div className="h-2 w-2 rounded-full bg-gray-300" />}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleBatchSync}
        disabled={isSyncing}
        className="flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-[13px] font-bold text-white transition-all active:scale-95 disabled:opacity-50"
        style={{ backgroundColor: "var(--highlightColor, #185FA5)" }}>
        {isSyncing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowPathIcon className="h-4 w-4" />}
        {isSyncing ? "Updating..." : "Sync All Changes"}
      </button>
    </div>
  );
};

const ToastStack = () => {
  const { toasts, removeToast } = useContext(ToastContext);
  if (!toasts.length) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[99999] flex flex-col items-end gap-2">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          {...t}
          removeToast={removeToast}
        />
      ))}
    </div>
  );
};

export default ToastStack;
